import { type ReactNode } from 'react';
import { usePermissionStore } from '../stores/permission.store';

interface CanProps {
	/** Single permission or list of permissions to check */
	permission: string | string[];
	/** When true, ALL permissions are required. Default: any one is enough. */
	requireAll?: boolean;
	/** Content to render when the user HAS permission */
	children: ReactNode;
	/** Optional content to render when the user does NOT have permission */
	fallback?: ReactNode;
}

/**
 * Declarative permission gate for component-level access control.
 *
 * Usage:
 *   <Can permission="iam:user:create">
 *     <Button>Create User</Button>
 *   </Can>
 *
 *   <Can permission={['iam:user:update', 'iam:user:delete']} fallback={<span>Read-only</span>}>
 *     <Button>Edit</Button>
 *   </Can>
 */
export function Can({ permission, requireAll = false, children, fallback = null }: CanProps) {
	const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissionStore();

	const permissions = Array.isArray(permission) ? permission : [permission];

	const allowed = requireAll
		? hasAllPermissions(permissions)
		: permissions.length === 1
			? hasPermission(permissions[0])
			: hasAnyPermission(permissions);

	return <>{allowed ? children : fallback}</>;
}
